

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PendingIcon from '@mui/icons-material/Pending';
import TabIcon from '@mui/icons-material/Tab';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { Storyboard } from '@/lib/storyboard-types';

export type ViewMode = 'tabs' | 'scroll';

interface StoryboardStatusBarProps {
  storyboard: Storyboard;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

export function StoryboardStatusBar({ storyboard, viewMode, onViewModeChange }: StoryboardStatusBarProps) {
  const { t } = useTranslation();
  // Calculate stats
  const totalPanels = storyboard.strips.reduce((sum, strip) => sum + strip.panels.length, 0);
  const acceptedPanels = storyboard.strips.reduce(
    (sum, strip) => sum + strip.panels.filter((p) => p.scriptStatus === 'accepted').length,
    0
  );
  const needsChangesPanels = storyboard.strips.reduce(
    (sum, strip) => sum + strip.panels.filter((p) => p.scriptStatus === 'needs_changes').length,
    0
  );
  const pendingPanels = totalPanels - acceptedPanels - needsChangesPanels;

  const progress = totalPanels > 0 ? Math.round((acceptedPanels / totalPanels) * 100) : 0;

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode) {
      onViewModeChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        mb: 2,
      }}
    >
      {/* Scene info */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('storyboard.status_bar.storyboard_for')}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {storyboard.sceneName}
        </Typography>
      </Box>

      {/* View Mode Toggle */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        size="small"
        sx={{
          bgcolor: 'action.hover',
          '& .MuiToggleButton-root': {
            border: 'none',
            px: 2,
          },
        }}
      >
        <ToggleButton value="tabs">
          <Tooltip title={t('storyboard.status_bar.accordion_view_tooltip')}>
            <TabIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="scroll">
          <Tooltip title={t('storyboard.status_bar.scroll_view_tooltip')}>
            <ViewColumnIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Chip
          icon={<CheckCircleIcon />}
          label={t('storyboard.status_bar.accepted_count', { count: acceptedPanels })}
          color="success"
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<EditIcon />}
          label={t('storyboard.status_bar.needs_changes_count', { count: needsChangesPanels })}
          color="warning"
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<PendingIcon />}
          label={t('storyboard.status_bar.pending_count', { count: pendingPanels })}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Progress */}
      <Box sx={{ textAlign: 'right', minWidth: 80 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {progress}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('storyboard.status_bar.complete')}
        </Typography>
      </Box>
    </Box>
  );
}
